import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Chip,
  FormControl,
  InputLabel,
  TextField,
  Typography,
  Avatar,
  useTheme,
  useMediaQuery,
  Checkbox,
  Popover,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  CircularProgress,
  FormHelperText,
} from '@mui/material';
import { TaskService } from '../../../service/task.service';
import zeplinColor from '../../../theme/Color';

interface AssigneeOption {
  Id: string;
  Name: string;
  Email?: string;
  profile_image_url?: string;
  avatar?: string;
}

interface AssigneeAutocompleteProps {
  value?: AssigneeOption | AssigneeOption[] | null;
  onChange: (value: AssigneeOption | AssigneeOption[] | null) => void;
  disabled?: boolean;
  placeholder?: string;
  loading?: boolean;
  multiple?: boolean;
  open?: boolean; // External control for popover open state
  onClose?: () => void; // Callback when popover closes
  hideField?: boolean; // Hide the field/button, only show popover
  anchorEl?: HTMLElement | null; // External anchor element for popover
  required?: boolean;
  label?: string;
  error?: string;
}

export const AssigneeAutocomplete: React.FC<AssigneeAutocompleteProps> = ({
  value,
  onChange,
  disabled = false,
  placeholder = 'Search people',
  loading: externalLoading = false,
  multiple = false,
  open: externalOpen,
  onClose: externalOnClose,
  hideField = false,
  anchorEl: externalAnchorEl,
  required = false,
  label,
  error,
}) => {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('xl'));
  const [assignOptions, setAssignOptions] = useState<AssigneeOption[]>([]);
  const [assignLoading, setAssignLoading] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [internalAnchorEl, setInternalAnchorEl] = useState<HTMLElement | null>(null);
  const fieldRef = useRef<HTMLDivElement>(null);

  // Use external anchor if provided, otherwise use internal
  const anchorEl = externalAnchorEl !== undefined ? externalAnchorEl : internalAnchorEl;

  // Use external open state if provided, otherwise use internal state
  const isControlled = externalOpen !== undefined;
  const [internalOpen, setInternalOpen] = useState(false);
  const open = isControlled ? externalOpen : internalOpen;

  const getAssignOptions = async () => {
    try {
      setAssignLoading(true);
      const res = await TaskService.getTaskDropdowns();

      const options = (res?.assignTo || []).filter(
        (option) => option && option.Id && option.Name,
      ) as AssigneeOption[];

      setAssignOptions(options);
    } catch (err) {
      console.error('Failed to fetch assignee options:', err);
      setAssignOptions([]);
    } finally {
      setAssignLoading(false);
    }
  };

  useEffect(() => {
    getAssignOptions();
  }, []);

  // Filter options based on search value
  const filteredOptions = useMemo(() => {
    if (!searchValue) return assignOptions;

    const searchLower = searchValue.toLowerCase();
    return assignOptions.filter((option) => {
      if (!option) return false;
      const nameMatch = option.Name?.toLowerCase().includes(searchLower) || false;
      const emailMatch = option.Email?.toLowerCase().includes(searchLower) || false;
      return nameMatch || emailMatch;
    });
  }, [assignOptions, searchValue]);

  // Check if an option is selected
  const isOptionSelected = (option: AssigneeOption) => {
    if (!normalizedValue) return false;
    if (multiple && Array.isArray(normalizedValue)) {
      return normalizedValue.some((selected: AssigneeOption) => selected.Id === option.Id);
    }
    if (!multiple && normalizedValue && !Array.isArray(normalizedValue)) {
      return normalizedValue.Id === option.Id;
    }
    return false;
  };

  const handleFieldClick = () => {
    if (!disabled && !hideField) {
      setInternalAnchorEl(fieldRef.current);
      if (!isControlled) {
        setInternalOpen(true);
      }
    }
  };

  const handleClose = () => {
    if (externalAnchorEl === undefined) {
      setInternalAnchorEl(null);
    }
    setSearchValue('');
    if (!isControlled) {
      setInternalOpen(false);
    }
    externalOnClose?.();
  };

  // Handle external open state changes
  useEffect(() => {
    if (isControlled && hideField && externalAnchorEl !== undefined) {
      // When using external anchor, don't manage anchorEl here
      if (!externalOpen) {
        setSearchValue('');
      }
    } else if (isControlled && !hideField) {
      if (externalOpen && fieldRef.current) {
        setInternalAnchorEl(fieldRef.current);
      } else if (!externalOpen) {
        setInternalAnchorEl(null);
        setSearchValue('');
      }
    }
  }, [externalOpen, isControlled, hideField, externalAnchorEl]);

  // Calculate open state: use controlled state if provided, otherwise use anchorEl
  const popoverOpen = isControlled ? open : Boolean(anchorEl);

  const handleOptionToggle = (option: AssigneeOption) => {
    if (!multiple) {
      // Single selection - replace current value
      onChange(isOptionSelected(option) ? null : option);
      handleClose();
    } else {
      // Multiple selection - toggle
      const currentValue = Array.isArray(normalizedValue) ? normalizedValue : [];
      const isSelected = isOptionSelected(option);

      if (isSelected) {
        // Remove from selection
        const newValue = currentValue.filter((item: AssigneeOption) => item.Id !== option.Id);
        onChange(newValue.length > 0 ? newValue : null);
      } else {
        // Add to selection
        onChange([...currentValue, option]);
      }
    }
  };

  const getInitials = (name: string) => {
    if (!name) return '';
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getAvatarUrl = (option: AssigneeOption) => {
    return option.avatar || option.profile_image_url || undefined;
  };

  // Normalize value for single or multiple selection
  const normalizedValue = useMemo(() => {
    if (!value) {
      return multiple ? [] : null;
    }
    if (multiple) {
      // For multiple mode, always return an array
      // Filter out empty objects (objects without Id or Name)
      const arr = Array.isArray(value) ? value : value ? [value] : [];
      return arr.filter((item: any) => item && item.Id && item.Name);
    } else {
      // For single mode, return single value or null
      const arr = Array.isArray(value) ? value : value ? [value] : [];
      const valid = arr.find((item: any) => item && item.Id && item.Name);
      return valid || null;
    }
  }, [value, multiple]);

  const showLabel = Boolean(label);
  const displayPlaceholder = showLabel ? placeholder : label || placeholder;

  // Render selected assignees display
  const renderSelectedDisplay = () => {
    if (multiple && Array.isArray(normalizedValue) && normalizedValue.length > 0) {
      // Show chips for multiple selection
      return (
        <Box sx={{ display: 'flex', gap: '4px', flexWrap: 'wrap', alignItems: 'center', flex: 1 }}>
          {normalizedValue.map((assignee: AssigneeOption) => {
            const avatarUrl = getAvatarUrl(assignee);
            return (
              <Chip
                key={assignee.Id}
                label={assignee.Name}
                avatar={
                  avatarUrl ? (
                    <Avatar src={avatarUrl} alt={assignee.Name} sx={{ width: 24, height: 24 }} />
                  ) : (
                    <Avatar
                      sx={{
                        width: 24,
                        height: 24,
                        bgcolor: zeplinColor.Brand1,
                        fontSize: '10px',
                      }}
                    >
                      {getInitials(assignee.Name)}
                    </Avatar>
                  )
                }
                onDelete={(e) => {
                  e.stopPropagation();
                  const newValue = normalizedValue.filter(
                    (item: AssigneeOption) => item.Id !== assignee.Id,
                  );
                  onChange(newValue.length > 0 ? newValue : null);
                }}
                deleteIcon={
                  <img
                    src='/assets/x-mark.svg'
                    alt='remove'
                    style={{ width: '18px', height: '18px' }}
                  />
                }
                sx={{
                  borderRadius: '30px',
                  backgroundColor: zeplinColor.Neutral3,
                  height: '32px',
                  '& .MuiChip-label': {
                    padding: '0 8px',
                    fontSize: isXs ? 14 : 16,
                  },
                  '& .MuiChip-avatar': {
                    marginLeft: '4px',
                  },
                }}
              />
            );
          })}
        </Box>
      );
    } else if (!multiple && normalizedValue && !Array.isArray(normalizedValue)) {
      // Show single assignee chip
      const avatarUrl = getAvatarUrl(normalizedValue);
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
          {avatarUrl ? (
            <Avatar src={avatarUrl} alt={normalizedValue.Name} sx={{ width: 24, height: 24 }} />
          ) : (
            <Avatar
              sx={{
                width: 24,
                height: 24,
                bgcolor: zeplinColor.Brand1,
                fontSize: '10px',
              }}
            >
              {getInitials(normalizedValue.Name)}
            </Avatar>
          )}
          <Typography
            variant='body3'
            color={zeplinColor.BasicBlack}
            sx={{ fontSize: isXs ? 14 : 16 }}
          >
            {normalizedValue.Name}
          </Typography>
        </Box>
      );
    }
    return (
      <Typography
        variant='body3'
        color={zeplinColor.Neutral8}
        sx={{ flex: 1, fontSize: isXs ? 14 : 16 }}
      >
        {displayPlaceholder} {required && <span style={{ color: zeplinColor.Error }}>*</span>}
      </Typography>
    );
  };

  // Render only Popover when hideField is true (no wrapper Box)
  if (hideField) {
    return (
      <Popover
        open={popoverOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        sx={{
          '& .MuiPaper-root': {
            borderRadius: '8px',
            boxShadow: 'rgba(0, 0, 0, 0.15)',
            marginTop: '8px',
            minWidth: '300px',
            maxWidth: '400px',
            maxHeight: '400px',
          },
        }}
      >
        <Box sx={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {/* Search Input */}
          <TextField
            placeholder='Search people...'
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            size={isXs ? 'small' : 'medium'}
            autoFocus
            InputProps={{
              startAdornment: (
                <img
                  src='/assets/search-icon.svg'
                  alt='search'
                  style={{ width: '20px', height: '20px', marginRight: '8px' }}
                />
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '50px',
              },
            }}
          />

          {/* Loading State */}
          {(assignLoading || externalLoading) && (
            <Box sx={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
              <CircularProgress size={24} />
            </Box>
          )}

          {/* Options List */}
          {!assignLoading && !externalLoading && (
            <List
              sx={{
                maxHeight: '300px',
                overflowY: 'auto',
                padding: 0,
                '&::-webkit-scrollbar': {
                  width: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  background: zeplinColor.Neutral5,
                  borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: zeplinColor.Neutral8,
                  borderRadius: '4px',
                },
              }}
            >
              {filteredOptions.length === 0 ? (
                <ListItem>
                  <ListItemText
                    primary={
                      <Typography variant='body2' color={zeplinColor.Neutral8}>
                        No assignees found
                      </Typography>
                    }
                  />
                </ListItem>
              ) : (
                filteredOptions.map((option) => {
                  const avatarUrl = getAvatarUrl(option);
                  const selected = isOptionSelected(option);

                  return (
                    <ListItem
                      key={option.Id}
                      disablePadding
                      onClick={() => handleOptionToggle(option)}
                    >
                      <ListItemButton
                        sx={{
                          padding: '12px 16px',
                          '&:hover': {
                            backgroundColor: zeplinColor.Neutral4,
                          },
                          backgroundColor: selected ? zeplinColor.Neutral4 : 'transparent',
                        }}
                      >
                        <Checkbox
                          checked={selected}
                          sx={{
                            padding: 0,
                            marginRight: '12px',
                            color: zeplinColor.BasicBlack,
                            '&.Mui-checked': {
                              color: zeplinColor.Brand1,
                            },
                          }}
                        />
                        {avatarUrl ? (
                          <Avatar
                            src={avatarUrl}
                            alt={option.Name}
                            sx={{ width: 32, height: 32, marginRight: '12px' }}
                          />
                        ) : (
                          <Avatar
                            sx={{
                              width: 32,
                              height: 32,
                              bgcolor: zeplinColor.Brand1,
                              fontSize: '12px',
                              marginRight: '12px',
                            }}
                          >
                            {getInitials(option.Name)}
                          </Avatar>
                        )}
                        <ListItemText
                          primary={
                            <Typography variant='body2' sx={{ fontWeight: 500 }}>
                              {option.Name}
                            </Typography>
                          }
                          secondary={
                            option.Email && (
                              <Typography variant='caption' color={zeplinColor.Neutral8}>
                                {option.Email}
                              </Typography>
                            )
                          }
                        />
                      </ListItemButton>
                    </ListItem>
                  );
                })
              )}
            </List>
          )}
        </Box>
      </Popover>
    );
  }

  return (
    <FormControl required={required} sx={{ flex: 1, position: 'relative' }}>
      {showLabel && (
        <InputLabel
          shrink
          required={required}
          sx={{
            position: 'absolute',
            top: -8,
            left: 12,
            backgroundColor: zeplinColor.Neutral1,
            px: '6px',
            fontSize: isXs ? 12 : 14,
            lineHeight: 1,
            zIndex: 1,
            color: zeplinColor.Neutral9,
            transform: 'none',
            '&.Mui-focused': { color: zeplinColor.Neutral9 },
            '& .MuiFormLabel-asterisk': { color: zeplinColor.Error },
          }}
        >
          {label}
        </InputLabel>
      )}
      {/* Field/Button that opens dropdown */}
      <Box
        ref={fieldRef}
        onClick={handleFieldClick}
        sx={{
          minHeight: '51px',
          border: `1px solid ${error ? zeplinColor.Error : zeplinColor.Neutral9}`,
          borderRadius: '14px',
          padding: '4px 16px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          cursor: disabled ? 'not-allowed' : 'pointer',
          backgroundColor: disabled ? zeplinColor.Neutral5 : zeplinColor.Neutral1,
          '&:hover': disabled
            ? {}
            : {
                borderColor: error ? zeplinColor.Error : zeplinColor.BasicBlack,
              },
        }}
      >
        {renderSelectedDisplay()}
        <img
          src='/assets/down-arrow.svg'
          alt='dropdown'
          style={{
            width: '20px',
            height: '20px',
            flexShrink: 0,
            transform: popoverOpen ? 'rotate(180deg)' : 'none',
            transition: 'transform 0.2s',
          }}
        />
      </Box>

      {/* Dropdown Popover */}
      <Popover
        open={popoverOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        sx={{
          '& .MuiPaper-root': {
            borderRadius: '8px',
            boxShadow: 'rgba(0, 0, 0, 0.15)',
            marginTop: '8px',
            minWidth: fieldRef.current?.offsetWidth || '300px',
            maxWidth: '400px',
            maxHeight: '400px',
          },
        }}
      >
        <Box sx={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {/* Search Input */}
          <TextField
            placeholder='Search people...'
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            size={isXs ? 'small' : 'medium'}
            autoFocus
            InputProps={{
              startAdornment: (
                <img
                  src='/assets/search-icon.svg'
                  alt='search'
                  style={{ width: '20px', height: '20px', marginRight: '8px' }}
                />
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '50px',
              },
            }}
          />

          {/* Loading State */}
          {(assignLoading || externalLoading) && (
            <Box sx={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
              <CircularProgress size={24} />
            </Box>
          )}

          {/* Options List */}
          {!assignLoading && !externalLoading && (
            <List
              sx={{
                maxHeight: '300px',
                overflowY: 'auto',
                padding: 0,
                '&::-webkit-scrollbar': {
                  width: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  background: zeplinColor.Neutral5,
                  borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: zeplinColor.Neutral8,
                  borderRadius: '4px',
                },
              }}
            >
              {filteredOptions.length === 0 ? (
                <ListItem>
                  <ListItemText
                    primary={
                      <Typography variant='body2' color={zeplinColor.Neutral8}>
                        No assignees found
                      </Typography>
                    }
                  />
                </ListItem>
              ) : (
                filteredOptions.map((option) => {
                  const avatarUrl = getAvatarUrl(option);
                  const selected = isOptionSelected(option);

                  return (
                    <ListItem
                      key={option.Id}
                      disablePadding
                      onClick={() => handleOptionToggle(option)}
                    >
                      <ListItemButton
                        sx={{
                          padding: '12px 16px',
                          '&:hover': {
                            backgroundColor: zeplinColor.Neutral4,
                          },
                          backgroundColor: selected ? zeplinColor.Neutral4 : 'transparent',
                        }}
                      >
                        <Checkbox
                          checked={selected}
                          sx={{
                            padding: 0,
                            marginRight: '12px',
                            color: zeplinColor.BasicBlack,
                            '&.Mui-checked': {
                              color: zeplinColor.Brand1,
                            },
                          }}
                        />
                        {avatarUrl ? (
                          <Avatar
                            src={avatarUrl}
                            alt={option.Name}
                            sx={{ width: 32, height: 32, marginRight: '12px' }}
                          />
                        ) : (
                          <Avatar
                            sx={{
                              width: 32,
                              height: 32,
                              bgcolor: zeplinColor.Brand1,
                              fontSize: '12px',
                              marginRight: '12px',
                            }}
                          >
                            {getInitials(option.Name)}
                          </Avatar>
                        )}
                        <ListItemText
                          primary={
                            <Typography variant='body2' sx={{ fontWeight: 500 }}>
                              {option.Name}
                            </Typography>
                          }
                          secondary={
                            option.Email && (
                              <Typography variant='caption' color={zeplinColor.Neutral8}>
                                {option.Email}
                              </Typography>
                            )
                          }
                        />
                      </ListItemButton>
                    </ListItem>
                  );
                })
              )}
            </List>
          )}
        </Box>
      </Popover>
      {error && (
        <FormHelperText error sx={{ marginTop: '4px', marginLeft: '14px' }}>
          {error}
        </FormHelperText>
      )}
    </FormControl>
  );
};
